// One process reviews the whole script: parse once, fan out over episodes
// in-memory, then converge single-threaded. Progress leaves as NDJSON so the
// caller can persist per episode without waiting for the run.
import * as fs from "node:fs";
import * as path from "node:path";
import * as sp from "@lingjingai/screenplay";
import { reviewUnit, type AgentConfig, type UnitResult, type UsageRow } from "./agent.js";

export interface ReviewOptions {
  scriptPath: string;
  source?: string;                 // video file or episode directory
  /**
   * Explicit per-episode videos, in episode order. Takes precedence over
   * `source`: a caller that already knows the mapping should pass it rather
   * than have it re-derived here, because two orderings drift (redraw parses
   * episode numbers, including Chinese numerals; `source` only sorts names).
   */
  videos?: (string | undefined)[];
  episodes?: number[];             // default: every episode
  concurrency?: number;
  converge?: boolean;
  convergeOnly?: boolean;
  apiKey: string;
  model?: string;
  mediaModel?: string;
  baseUrl?: string;
  maxSteps?: number;
  mediaBudget?: number;
  episodeTimeoutMs?: number;
  onEvent?: (event: ReviewEvent) => void;
}

export type ReviewEvent =
  | { type: "unit-started"; episode_number: number }
  | ({ type: "episode" | "global" } & UnitResult)
  | { type: "done"; statistics: Statistics; usage_total: UsageRow[]; wall_ms: number };

export interface Statistics {
  fixed: number; unchanged: number; unresolved: number; failed_episodes: number;
}

/** Positional pairing of sorted videos with episodes, as redraw does. */
export function episodeVideos(source: string | undefined, count: number): (string | undefined)[] {
  if (!source) return Array.from({ length: count }, () => undefined);
  const stat = fs.statSync(source);
  if (stat.isFile()) return Array.from({ length: count }, () => source);
  const files = fs.readdirSync(source)
    .filter((name) => /\.(mp4|mov|mkv|m4v)$/i.test(name))
    .sort()
    .map((name) => path.join(source, name));
  if (files.length < count) {
    throw new Error(`${source} holds ${files.length} videos for ${count} episodes`);
  }
  return files.slice(0, count);
}

function statistics(units: UnitResult[]): Statistics {
  const findings = units.flatMap((unit) => unit.findings);
  return {
    fixed: findings.filter((f) => f.outcome === "fixed").length,
    unchanged: findings.filter((f) => f.outcome === "unchanged").length,
    unresolved: findings.filter((f) => f.outcome === "unresolved").length,
    failed_episodes: units.filter((u) => u.state === "failed" && u.episode_number > 0).length,
  };
}

/** Bounded fan-out. Node is single-threaded, so an in-flight episode is just an await. */
async function pool<T>(items: T[], limit: number, run: (item: T) => Promise<void>): Promise<void> {
  const queue = [...items];
  const workers = Array.from({ length: Math.max(1, Math.min(limit, queue.length)) }, async () => {
    for (;;) {
      const item = queue.shift();
      if (item === undefined) return;
      await run(item);
    }
  });
  await Promise.all(workers);
}

export async function reviewScript(opts: ReviewOptions): Promise<ReviewEvent[]> {
  const started = Date.now();
  // Parse once. Every tool mutates this document in memory; nothing is written
  // until commit, so there is no lock and no plan digest to arbitrate writers.
  const doc = sp.parse(JSON.parse(fs.readFileSync(opts.scriptPath, "utf8")));
  const config: AgentConfig = {
    apiKey: opts.apiKey,
    model: opts.model ?? "gemini-3.8-flash",
    mediaModel: opts.mediaModel ?? opts.model ?? "gemini-3.8-flash",
    ...(opts.baseUrl ? { baseUrl: opts.baseUrl } : {}),
    maxSteps: opts.maxSteps ?? 60,
    mediaBudget: opts.mediaBudget ?? 1,
    timeoutMs: opts.episodeTimeoutMs ?? 8 * 60_000,
  };

  const all = doc.episodes.map((ep) => ep.no);
  const selected = opts.convergeOnly ? [] : opts.episodes?.length ? opts.episodes.filter((n) => all.includes(n)) : all;
  const videos = opts.videos?.length
    ? opts.videos
    : episodeVideos(opts.source, doc.episodes.length);
  const events: ReviewEvent[] = [];
  const emit = (event: ReviewEvent) => { events.push(event); opts.onEvent?.(event); };

  const units: UnitResult[] = [];
  // A reported unit must be a durable one: the caller treats a line as "this
  // episode is done" and will not re-run it, so the document reaches disk
  // before the line goes out. Committing once at the end instead made a
  // crashed run lose every edit while its already-reported findings still
  // claimed them.
  //
  // The snapshot covers the whole document, so it also carries whatever the
  // still-running episodes have changed so far. That is unavoidable with one
  // shared document and harmless: mutations are synchronous, so a commit can
  // only land between two tool calls, never inside one. An episode that never
  // reported is re-run, and the agent re-derives what to fix from the current
  // text rather than replaying a plan — which is why this needs no digest.
  const checkpoint = (unit: UnitResult, type: "episode" | "global") => {
    units.push(unit);
    sp.commit(doc, opts.scriptPath);
    emit({ type, ...unit });
  };

  await pool(selected, opts.concurrency ?? 5, async (episode) => {
    emit({ type: "unit-started", episode_number: episode });
    checkpoint(await reviewUnit(doc, episode, videos[episode - 1], config), "episode");
  });

  // Convergence runs alone and last: it is the only phase allowed to touch
  // global assets, which is what keeps two episodes from merging the same
  // duplicate actor concurrently.
  if (opts.convergeOnly || opts.converge !== false) {
    emit({ type: "unit-started", episode_number: 0 });
    checkpoint(await reviewUnit(doc, 0, undefined, config), "global");
  }

  const usage_total = units.flatMap((unit) => unit.usage);
  emit({
    type: "done",
    statistics: statistics(units),
    usage_total,
    wall_ms: Date.now() - started,
  });
  return events;
}
